import {action, computed, observable} from 'mobx';
import {MagicMarkerOptions} from '@techsee/techsee-nway-media-service-client';
import {IContainerPoint} from '@techsee/techsee-nway-media-service-client/lib/components/MagicMarker/MagicMarkerComponent';
import {LOG_EVENTS} from '@techsee/techsee-common/lib/constants/event-logs.constants';
import {IChatApi} from '../../../../../services/ts-chat-api/ChatApi';
import {UserType} from '@techsee/techsee-common/lib/constants/room.constants';

export class MagicMarkerController {
    @observable public remoteDrawings: IContainerPoint[] = [];
    @observable private videoDimensions: {width: number; height: number} = {width: 640, height: 480};
    @observable private enabled = false;
    @observable private isMirrored = false;
    @observable private currentDrawingSource: UserType | null = null;

    constructor(
        private tsChatApi: IChatApi,
        public readonly isMobile: boolean,
        private readonly sendEventLog: (eventType: string, meta?: Record<string, unknown>) => Promise<void>
    ) {
        this.setupEvents();
    }

    private setupEvents = () => {
        this.tsChatApi.on('clientDrawings', (propName: string, value: IContainerPoint[]) => {
            if (value.length) {
                this.sendEventLog(LOG_EVENTS.magicMarkerDrawingReceived);
            }

            this.handleClientDrawings(value);
        });

        this.tsChatApi.on('dashboardDrawings', (propName: string, value: IContainerPoint[]) => {
            if (value.length) {
                this.sendEventLog(LOG_EVENTS.magicMarkerDrawingReceived);
            }

            this.handleDashboardDrawings(value);
        });
    };

    @action
    private handleClientDrawings = (drawings: IContainerPoint[]) => {
        this.currentDrawingSource = UserType.client;
        this.remoteDrawings = drawings;
    };

    @action
    private handleDashboardDrawings = (drawings: IContainerPoint[]) => {
        this.currentDrawingSource = UserType.dashboard;
        this.remoteDrawings = drawings;
    };

    @action
    public clearRemoteDrawings = () => {
        this.remoteDrawings = [];
    };

    public sendDrawings = (drawings: IContainerPoint[]) => {
        this.tsChatApi.setStatusFast('drawings', drawings, true);

        if (drawings.length) {
            this.sendEventLog(LOG_EVENTS.magicMarkerDrawingSent);
        }
    };

    @action
    public setVideoDimensions = (dimensions: {width: number; height: number}) => {
        if (this.videoDimensions.width === dimensions.width && this.videoDimensions.height === dimensions.height) {
            return;
        }

        this.videoDimensions = {...dimensions};

        this.clearRemoteDrawings();
    };

    @action
    public setEnabled = (enabled: boolean) => {
        this.enabled = enabled;
    };

    @action
    public setMirrored = (isMirrored: boolean) => {
        this.isMirrored = isMirrored;
    };

    @computed
    public get options(): MagicMarkerOptions {
        const dashboardSettings = this.tsChatApi.accountSettings?.magicMarker?.dashboard;
        const clientSettings = this.tsChatApi.accountSettings?.magicMarker?.client;
        const remoteSettings = this.currentDrawingSource === UserType.dashboard ? dashboardSettings : clientSettings;

        return {
            drawingOptions: {
                lineWidth: dashboardSettings?.lineWidth,
                lineWidthRefResolution: this.videoDimensions,
                strokeColorRGB: dashboardSettings?.strokeColorRgb
            },
            timeUntilDrawingStartFading: dashboardSettings?.timeUntilDrawingStartFading,
            drawingOptionsRemote: {
                lineWidth: remoteSettings?.lineWidth,
                lineWidthRefResolution: this.videoDimensions,
                strokeColorRGB: remoteSettings?.strokeColorRgb
            },
            timeUntilDrawingStartFadingRemote: remoteSettings?.timeUntilDrawingStartFading,
            isMobile: this.isMobile,
            canDraw: dashboardSettings?.enable && !this.tsChatApi.observer,
            isMirrored: this.isMirrored
        };
    }

    @computed
    public get shouldDisplayComponent() {
        return (
            this.tsChatApi.accountSettings?.magicMarker?.enabledInFacemeet &&
            this.tsChatApi.accountSettings?.magicMarker?.dashboard.enable &&
            this.enabled
        );
    }
}
